import makeStyles from '@material-ui/core/styles/makeStyles';
import { useEffect, useState } from 'react';
import { s8sColor } from '../common/themes';
import IconSpeaker from './IconSpeaker';

const convertSpecialNumbersForTTS = (input: string, locale: string) => {
	if (!locale.startsWith('zh-')) {
		return input;
	}

	return input
		.replace(/(^|[^\d])165(?!\.\d)(?=[^\d]|$)/g, '$1一六五')
		.replace(/(^|[^\d])110(?!\.\d)(?=[^\d]|$)/g, '$1一一零')
		.replace(/(^|[^\d])119(?!\.\d)(?=[^\d]|$)/g, '$1一一九');
};

const findMatchingVoice = (locale: string) => {
	if (!('speechSynthesis' in window)) {
		return null;
	}

	const voices = window.speechSynthesis.getVoices();
	if (!voices.length) {
		return null;
	}

	const normalizedLocale = locale.toLowerCase();
	const languagePrefix = normalizedLocale.split('-')[0];

	return (
		voices.find(voice => voice.lang?.toLowerCase() === normalizedLocale) ||
		voices.find(voice => voice.lang?.toLowerCase().startsWith(`${languagePrefix}-`)) ||
		voices.find(voice => voice.lang?.toLowerCase() === languagePrefix) ||
		null
	);
};

interface TextWithSpeakerProps {
	text: string;
	showSpeaker: boolean;
	className: string;
	replyLocale?: string;
	fromMe?: string | null;
}

const useStyles = makeStyles(
	() => ({
		msbubble: {
			'@media (min-width: 200px)': {
				maxWidth: '95%',
			},
			'@media (min-width: 500px)': {
				maxWidth: '90%',
			},
		},
	}),
	{
		name: 'MessageBubble',
	}
);

const TextWithSpeaker = ({ text, showSpeaker, className, replyLocale, fromMe }: TextWithSpeakerProps) => {
	const classes = useStyles();
	const [isPlaying, setIsPlaying] = useState(false);
	// 唯一識別此元件
	const id = useState(() => Math.random().toString(36).substr(2, 9))[0];
	const speechLocale = replyLocale || 'zh-TW';

	// 抽出停止語音的共用函式
	const stopPlaying = () => {
		if ('speechSynthesis' in window) {
			window.speechSynthesis.cancel();
		}
		setIsPlaying(false);
	};

	// 將文字轉語音
	const handlePlay = () => {
		if (isPlaying) {
			stopPlaying();
			return;
		}

		// 通知其他 TextWithSpeaker 停止
		window.dispatchEvent(new CustomEvent('text-with-speaker-play', { detail: id }));

		if ('speechSynthesis' in window) {
			// 移除 HTML 標籤,只保留純文字
			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = text;
			const plainText = tempDiv.textContent || tempDiv.innerText || '';
			const ttsText = convertSpecialNumbersForTTS(plainText, speechLocale);

			const utter = new window.SpeechSynthesisUtterance(ttsText);
			const matchedVoice = findMatchingVoice(speechLocale);
			utter.lang = matchedVoice?.lang || speechLocale;
			if (matchedVoice) {
				utter.voice = matchedVoice;
			}
			utter.onend = () => setIsPlaying(false);
			utter.onerror = () => setIsPlaying(false);
			setIsPlaying(true);
			window.speechSynthesis.speak(utter);
		}
	};

	// 偵聽全域事件，停止自己
	useEffect(() => {
		const handleGlobalPlay = (e: CustomEvent) => {
			if (e.detail !== id && isPlaying) {
				stopPlaying();
			}
		};
		window.addEventListener('text-with-speaker-play', handleGlobalPlay as EventListener);
		return () => {
			window.removeEventListener('text-with-speaker-play', handleGlobalPlay as EventListener);
		};
	}, [isPlaying, id]);

	return (
		<div
			className={`${classes.msbubble}`}
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: 8,
				flexDirection: fromMe ? 'row-reverse' : 'row',
				maxWidth: '90%',
			}}
		>
			<div className={className} style={{ flex: '0 1 auto', maxWidth: '100%' }} dangerouslySetInnerHTML={{ __html: text }} />
			{showSpeaker && (
				<div style={{ cursor: 'pointer' }} onClick={handlePlay}>
					<IconSpeaker iconColor={isPlaying ? s8sColor.brand.hover : s8sColor.neutral.gray800} width={22} height={22} />
				</div>
			)}
		</div>
	);
};

export default TextWithSpeaker;

TextWithSpeaker.defaultProps = {
	replyLocale: undefined,
	fromMe: null,
};
